import { useEffect, useState } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  User,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import getUserSession from 'features/auth/api/getUserSession';
import signUpWithGoogle from 'features/auth/api/signUpWithGoogle';
import serverErrorHandler from 'services/serverError.service';
import { openInfoToaster } from 'services/toast.service';
import getSclaApp from 'features/scla/api/getSclaApp';
import { loadSclaApplicationYears } from 'features/scla/api/getSclaApplicationYears';
import { resolveSclaApplication } from 'utils/sclaApplicationKind';
import useUserStore from 'store/useUserStore';
import { setPendingRedirectCredential } from 'utils/redirectCredentialStore';
import { setTokenFallbackUser } from 'utils/firebase.util';
import { registerAuthSessionSync } from 'utils/authSessionSync';

function isGoogleProvider(user: { providerData?: Array<{ providerId?: string }> }) {
  return (user.providerData ?? []).some(p => p.providerId === 'google.com');
}

function getErrorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number }; status?: number })?.response
    ?.status
    ?? (error as { status?: number })?.status;
}

function getErrorMessage(error: unknown): string {
  return (
    (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ?? ''
  );
}

function isMissingToken(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === 400) return true;
  return status === 403 && /missing auth token/i.test(getErrorMessage(error));
}

/**
 * This hook will fetch user profile data for the logged in user.
 * The data will only be fetched if there is user session token
 * but no user session profile data
 */
export default function useAuth() {
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    let generation = 0;

    getRedirectResult(auth).catch((e: unknown) => {
      if (!(e instanceof FirebaseError)) return;
      if (e.code === 'auth/account-exists-with-different-credential') {
        const credential = GoogleAuthProvider.credentialFromError(e);
        const email = (e.customData?.email as string) ?? null;
        setPendingRedirectCredential(credential, email);
        return;
      }
      if (
        e.code === 'auth/web-storage-unsupported' ||
        e.code === 'auth/operation-not-supported-in-this-environment'
      ) {
        return;
      }
      serverErrorHandler(e);
    });

    // An email account that later used Google keeps its user id as the uid.
    // If Firebase dropped the unverified password, say how to get it back.
    function notePasswordDropped(firebaseUser: {
      uid?: string;
      providerData?: Array<{ providerId?: string }>;
    }) {
      const providers = (firebaseUser.providerData || []).map(p => p.providerId);
      const emailAccount = /^[a-f0-9]{24}$/.test(firebaseUser.uid || '');
      if (!emailAccount || providers.includes('password') || !providers.includes('google.com')) {
        return;
      }
      const key = `my-txt-google-only-note-${firebaseUser.uid}`;
      try {
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, '1');
      } catch {
        // Show the note anyway if storage is blocked.
      }
      openInfoToaster(
        'You can now sign in with Google. To use a password too, use Forgot password on the sign-in page.',
        10000,
      );
    }

    async function loadAccount(firebaseUser: User) {
      try {
        return await getUserSession();
      } catch (accountError) {
        if (!isMissingToken(accountError)) throw accountError;
        await firebaseUser.getIdToken(true);
        return getUserSession();
      }
    }

    async function fetchUserAsync(firebaseUser: User) {
      const id = generation + 1;
      generation = id;
      setTokenFallbackUser(firebaseUser);
      if (!useUserStore.getState().user) setIsDone(false);

      try {
        let response;
        try {
          response = await loadAccount(firebaseUser);
        } catch (accountError) {
          const status = getErrorStatus(accountError);
          const message = getErrorMessage(accountError);
          const accountMissing =
            status === 403 && /account does not exist/i.test(message);

          if (accountMissing && isGoogleProvider(firebaseUser)) {
            try {
              await signUpWithGoogle();
              response = await getUserSession();
            } catch (provisionError) {
              if (getErrorStatus(provisionError) === 409) {
                if (id !== generation) return;
                const email = firebaseUser.email ?? null;
                await signOut(auth);
                // Credential is not available here after a successful Google
                // popup; surface email so the login form can prompt to link.
                setPendingRedirectCredential(null, email);
                return;
              }
              throw provisionError;
            }
          } else {
            throw accountError;
          }
        }

        if (id !== generation) return;

        // Leave the sign-in page before the slower application fetch.
        useUserStore.getState().setUser(response);
        notePasswordDropped(firebaseUser);

        if (resolveSclaApplication(response.type).allowed) {
          try {
            const years = await loadSclaApplicationYears();
            if (id !== generation) return;
            if (years.currentCohortYear) {
              const application = await getSclaApp(years.currentCohortYear);
              if (application && id === generation) {
                useUserStore.getState().setUserApplication(application);
              }
            }
          } catch {
            // The home page loads the application again if this fails.
          }
        }
      } catch (e) {
        if (id === generation) serverErrorHandler(e);
      } finally {
        if (id === generation) setIsDone(true);
      }
    }

    const unsubscribeAuth = onAuthStateChanged(auth, user => {
      if (user) {
        fetchUserAsync(user);
        return;
      }
      generation += 1;
      setTokenFallbackUser(null);
      useUserStore.getState().logout();
      setIsDone(true);
    });

    const unregisterSync = registerAuthSessionSync(user => {
      fetchUserAsync(user);
    });

    // Popup sign-in writes the Firebase user even when the auth listener
    // does not run again (same uid, or the first profile fetch failed).
    const onFocus = () => {
      const current = auth.currentUser;
      if (current && !useUserStore.getState().user) {
        fetchUserAsync(current);
      }
    };
    window.addEventListener('focus', onFocus);

    return () => {
      generation += 1;
      unsubscribeAuth();
      unregisterSync();
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return { isDone };
}
